import { Fragment, useMemo } from "react";
import ProductCard from "@/components/ProductCard";
import { FormatSubType } from "@/constants/tagular/main";
import getUserContextFromClient from "@/lib/user-context/getUserContextFromClient";
import useSortedProductSummaries from "@/hooks/useSortedProductSummaries";
import SearchResultsLoadingPlaceholder from "./ResultsLoading";
import {
  SortProductSummariesRequest,
  SortProductSummaryInput,
} from "@/common/productSummary/types/SortProductSummary";
import type { PromoBannerProps } from "@/components/PromoBanner/types";
import { getWebContext } from "@/utils/tagular/helpers";
import { v4 as uuid } from "uuid";
import { getSearchQueryId } from "@/utils/getSearchQueryId";
import SearchHitsPromoBanner from "../SearchHitsPromoBanner";
import { ProductCardProps } from "@/components/ProductCard/types";
import type { InlineFypConfig } from "./types";
import useInlineRowPosition from "./useInlineRowPosition";
import InlineFypBlock from "./InlineFypBlock";

interface SortedSearchHitsProps {
  hits: ProductCardProps[];
  results: any;
  facetForLink?: string;
  level?: string;
  searchPageType?: string;
  promoBannerData?: PromoBannerProps;
  cardClassName?: string | undefined;
  inlineFypConfig?: InlineFypConfig;
}

const convertToSortInput = (hit: ProductCardProps): SortProductSummaryInput => {
  return {
    productUuid: hit.uuid,
    productName: hit.name,
    productType: hit.type as any,
    productCategory: hit.product,
    partnerKeys: Array.isArray(hit.partnerKey)
      ? hit.partnerKey
      : hit.partnerKey
        ? [hit.partnerKey]
        : [],
  };
};

function SortedSearchHits({
  hits,
  results,
  facetForLink = "",
  level = "first",
  searchPageType = "multi-lob",
  promoBannerData,
  cardClassName,
  inlineFypConfig,
}: SortedSearchHitsProps) {
  // Generate fresh list ID for each component instance (represents this specific list/page)
  const listId = useMemo(() => uuid(), []);
  // fetch search query id - generated on search submit
  const searchQueryId = getSearchQueryId();
  const sortRequest: SortProductSummariesRequest | null = useMemo(() => {
    if (!hits.length) return null;

    const productSummaries = hits.map((hit) => convertToSortInput(hit));
    const clientUserContext = getUserContextFromClient();

    return {
      productSummaries,
      userContext: {
        country: clientUserContext.country || "",
        isMobile: clientUserContext.isMobile || false,
        region: clientUserContext.region || "",
        searchPageType: searchPageType,
      },
      webContext: getWebContext(),
      listId,
      searchQueryId: searchQueryId || "",
    };
  }, [searchPageType, hits, listId, searchQueryId]);

  const {
    data: sortedResponse,
    isLoading,
    error,
  } = useSortedProductSummaries(sortRequest);

  const inlinePosition = useInlineRowPosition(inlineFypConfig?.afterRow ?? 0);

  const sortedHits = useMemo(() => {
    if (!sortedResponse?.length || isLoading || error) {
      return hits;
    }

    const sortedPositionMap = new Map(
      sortedResponse.map((item) => [item.productUuid, item.sortPosition]),
    );

    return [...hits].sort((a, b) => {
      const positionA = sortedPositionMap.get(a.uuid) || 999;
      const positionB = sortedPositionMap.get(b.uuid) || 999;
      return positionA - positionB;
    });
  }, [hits, sortedResponse, isLoading, error]);

  return (
    <>
      {isLoading ? (
        <div className="md:col-span-2 lg:col-span-3">
          <SearchResultsLoadingPlaceholder cardCount={hits.length} />
        </div>
      ) : (
        <>
          {sortedHits.map((hit, index) => (
            <Fragment key={hit.uuid}>
              <div key={hit.uuid} className="flex justify-center">
                <ProductCard
                  {...hit}
                  className={cardClassName || (facetForLink ? "" : "w-full")}
                  location="search-results"
                  index={index}
                  formatSubtype={FormatSubType.HorizontallyStacked}
                  resultLevel={level}
                  listId={listId}
                  searchQueryId={searchQueryId}
                />
              </div>
              {promoBannerData && index <= 3 && (
                <SearchHitsPromoBanner
                  data={promoBannerData}
                  position={index}
                  level={level}
                />
              )}
              {inlineFypConfig && (
                <InlineFypBlock
                  config={inlineFypConfig}
                  inlinePosition={inlinePosition}
                  currentIndex={index}
                />
              )}
            </Fragment>
          ))}
        </>
      )}
    </>
  );
}

export default SortedSearchHits;
